import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Link, useLocation } from "react-router-dom";
import { Home } from "lucide-react";

interface BreadcrumbNavigationProps {
  customPath?: Array<{ name: string; href: string }>;
  currentPage?: string;
}

const BreadcrumbNavigation = ({ customPath, currentPage }: BreadcrumbNavigationProps) => {
  const location = useLocation();
  
  // Generate breadcrumbs from current path if no custom path provided
  const generateBreadcrumbs = () => {
    if (customPath) {
      return customPath;
    }
    
    // Only allow breadcrumbs for the 5 main pages
    const validPaths = [
      '/',
      '/products', 
      '/collections',
      '/collections/goddess',
      '/collections/chrome-hearts'
    ];
    
    if (!validPaths.includes(location.pathname)) {
      return [];
    }
    
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [];
    
    // Build breadcrumbs only for valid paths
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      const href = '/' + pathSegments.slice(0, i + 1).join('/');
      
      // Convert segment to readable name
      let name = segment.charAt(0).toUpperCase() + segment.slice(1);
      name = name.replace(/-/g, ' ');
      
      // Special cases for the 5 main pages
      if (segment === 'products') name = 'Products';
      if (segment === 'collections') name = 'Collections';
      if (segment === 'goddess') name = 'Goddess Collection';
      if (segment === 'chrome-hearts') name = 'Chrome Hearts';
      
      breadcrumbs.push({ name, href });
    }
    
    return breadcrumbs;
  };
  
  const breadcrumbs = generateBreadcrumbs();
  const finalPage = currentPage || (breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].name : '');
  
  // Don't show breadcrumbs on homepage
  if (location.pathname === '/') {
    return null;
  }
  
  return (
    	<div className="page-container bg-muted/30 body-xs leading-tight">
      <Breadcrumb className="body-xs leading-tight">
        <BreadcrumbList className="body-xs">
          {/* Home link */}
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/" className="flex items-center gap-1 body-xs leading-none h-3">
                <Home className="h-2 w-2 flex-shrink-0 self-center" />
                Home
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          
          {breadcrumbs.length > 0 && <BreadcrumbSeparator />}
          
          {/* Breadcrumb links */}
          {breadcrumbs.slice(0, -1).map((crumb, index) => (
            <div key={crumb.href} className="flex items-center">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={crumb.href} className="body-xs">{crumb.name}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </div>
          ))}
          
          {/* Current page */}
          {finalPage && (
            <BreadcrumbItem>
              <BreadcrumbPage className="body-xs weight-medium">{finalPage}</BreadcrumbPage>
            </BreadcrumbItem>
          )}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
};

export default BreadcrumbNavigation;